import React from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Snippet } from "react-instantsearch";

/**
 * Truncates a string to a specified length without breaking words and appends an ellipsis.
 * If the specified truncation length falls within a word, it will truncate to the previous word boundary
 *   (space, newline, or carriage return).
 * If there's no word boundary before the specified length, it truncates directly at the given length.
 *
 * @param {string} str - The string to be truncated.
 * @param {number} maxLength - The maximum length of the truncated string, excluding the appended ellipsis.
 * @returns {string} The truncated string with an appended ellipsis if truncation occurred, or the original string if
 *   no truncation was needed.
 */
function beautifulTruncateStr(str, maxLength) {
  if (!str || !maxLength || maxLength < 1 || str.length <= maxLength + 3) {
    return str;
  }

  if ([" ", "\r", "\n"].includes(str[maxLength])) {
    return `${str.substr(0, maxLength).trim()}...`;
  }

  const tmpStr = str.slice(0, maxLength);
  const arr = [
    tmpStr.lastIndexOf(" ", maxLength),
    tmpStr.lastIndexOf("\r", maxLength),
    tmpStr.lastIndexOf("\n", maxLength),
  ];
  const spaceCharacterIndex = Math.max(...arr);

  if (spaceCharacterIndex > 0) {
    return `${str.substr(0, spaceCharacterIndex)}...`;
  }

  return `${str.substr(0, maxLength)}...`;
}

/**
 * SmartSnippet gives a "smart" way to display a snippet of matched content from a search result. If
 * the search match starts in the middle of the content, it adds ellipses to indicate continuation before
 * and after the matched snippet. If no matched snippet is provided, it gracefully truncates the content
 * for a clean presentation.
 */
function SmartSnippet(props) {
  const { attribute, hit, truncateLength } = props;

  if (Object.prototype.hasOwnProperty.call(hit, "_snippetResult")) {
    // eslint-disable-next-line no-underscore-dangle
    const snippetAttribute = (_.get(hit._snippetResult, attribute) || {}).value;
    const snippetText = (snippetAttribute || "").replace(/<[^>]*>/g, "");
    const originalText = (_.get(hit, attribute) || "").replace(/<[^>]*>/g, "");
    const showOpeningEllipsis = snippetText.substring(0, 10) !== originalText.substring(0, 10);

    return (
      <>
        {showOpeningEllipsis && <span>...</span>}
        <Snippet {...props} />
        <span>...</span>
      </>
    );
  }

  return beautifulTruncateStr(hit[attribute], truncateLength);
}

SmartSnippet.propTypes = {
  attribute: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  hit: PropTypes.instanceOf(Object).isRequired,
};

export default SmartSnippet;
